import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useRestaurantContext } from 'context/RestaurantContext';
import useCart from 'hooks/useCart';
import { useAuth } from '../../context/AuthContext';

const BottomTabNavigation = ({ className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurantContext();
  const { cartData } = useCart();
  const { currentUser, isGuest } = useAuth(); // Get user authentication status
  const cartCount = cartData.itemCount;

  const allTabs = [
    {
      id: 'restaurant',
      label: 'restaurant',
      icon: 'Home',
      path: restaurantId ? '/restaurant' : null,
    },
    {
      id: 'menu',
      label: 'Menu',
      icon: 'Menu',
      path: restaurantId ? `/restaurant/${restaurantId}` : null,
    },
    {
      id: 'cart',
      label: 'Cart',
      icon: 'ShoppingCart',
      path: restaurantId ? `/restaurant/${restaurantId}/checkout` : null,
      badge: cartCount > 0 ? cartCount : null,
      requiresAuth: true, // This tab requires authentication
    },
    {
      id: 'orders',
      label: 'My Orders',
      icon: 'Receipt',
      path: restaurantId ? `/restaurant/${restaurantId}/orders` : null,
      requiresAuth: true, // This tab requires authentication
    },
  ];

  const tabs = allTabs.filter(tab => {
    if (tab.id === 'cart') {
      return true; // Always show cart
    }
    if (tab.requiresAuth) {
      return currentUser;
    }
    return true;
  });

  const handleTabClick = (tab) => {
    if (tab.path) {
      navigate(tab.path);
    }
  };

  const isActiveTab = (tabPath) => {
    if (!tabPath) return false;
    return location.pathname === tabPath;
  };

  return (
    <>
      {/* Desktop Sidebar Navigation */}
      <nav className={`hidden lg:flex fixed left-0 top-10 bottom-0 w-60 bg-card border-r border-border z-navigation flex-col ${className}`}>
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="QrCode" size={20} color="white" />
            </div>
          <div>
              <h1 className="text-lg font-semibold text-foreground">Swado</h1>
              <p className="text-sm text-muted-foreground">Restaurant Ordering</p>
            </div>
          </div>
        </div>

        <div className="flex-1 py-6">
          <div className="px-3 space-y-1">
            {tabs?.map((tab) => {
              const isActive = isActiveTab(tab.path);
              const isDisabled = !tab.path;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  disabled={isDisabled}
                  className={`relative w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
                    isActive
                      ? 'bg-primary/10 text-primary border-r-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label={tab.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon 
                    name={tab.icon} 
                    size={20} 
                    className="mr-3"
                  />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {tab.badge && (
                    <span className="ml-2 min-w-[20px] h-5 bg-accent text-accent-foreground text-xs font-medium rounded-full flex items-center justify-center px-1.5 animate-scale-in">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6 border-t border-border">
          <div className="text-xs text-muted-foreground">
            © 2025 Swado
          </div>
        </div>
      </nav>
    </>
  );
};

export default BottomTabNavigation;
